import { createElement } from 'react';
import dayjs from 'dayjs';
import { CURRENCY_CODE_TO_SYMBOL, CURRENCY_SYMBOL, SERVICES_KEYS, FREIGHT_METHODS, STAND_OPTIONS, CBM_PRICE_RANGE_CALCULATION } from './constants';

const mode: any = import.meta.env.VITE_MODE;

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDate = (date: string, format: string = "DD MMM YYYY"): any => {
  try {
    if (!date || !dayjs(date).isValid()) {
      return "Invalid Date";
    }
    return dayjs(date).format(format);
  } catch (error) {
    console.error("Error in formatDate", error);
    return "Invalid Date";
  }
};


// Generate initials from name
export const getInitials = (name: string): string => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Debounce function
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Generate random ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Validate email
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone number
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// Get status color
export const getStatusColor = (status: string): string => {
  const colors: { [key: string]: string } = {
    active: '#52c41a',
    inactive: '#ff4d4f',
    pending: '#faad14',
    completed: '#1890ff',
    draft: '#d9d9d9',
  };

  return colors[status.toLowerCase()] || '#d9d9d9';
};

// Split status string to handle combined statuses like: "Submitted + Paid"
export const splitStatusText = (text: unknown): string[] => {
  if (typeof text === 'string') {
    return text
      .split(/[+,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  if (text === null || text === undefined) return [];

  const val = String(text).trim();
  return val ? [val] : [];
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Truncate text
export const truncate = (text: string, length: number): string => {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
};

export const logHelper = (tag: string, message: string, value: any) => {
  // console.log("" + tag, " ===> " + key, value);
  if (mode === 'development') {
    try {
      console.log('🟢 ' + tag, " =====> ", message, value);
      // console.trace();
    } catch (error) {
      console.log("🟢", message);
    }
  }
};

export const regionListToOptions = () => {
  const regions = [
    { label: "EUROPE", value: "EUROPE" },
    { label: "UK", value: "UK" },
    { label: "USA", value: "USA" },
    { label: "US HANSON WADE", value: "US_HANSON_WADE" },
    { label: "OTHERS", value: "OTHERS" },
    { label: "US OXFORD GLOBAL", value: "US_OXFORD_GLOBAL" }
  ]
  return regions;
}

export const organiserListToOptions = (organiserList: any) => {
  if (!organiserList) return [];
  return organiserList.map((organiser: any) => ({ label: organiser.name, value: organiser.id }));
};

export const getExhibitorDisplayName = (exhibitor: any) => {
  if (!exhibitor) return '-';
  return exhibitor?.contact_name || exhibitor?.name || exhibitor?.company_name || '-';
};

export const getExhibitorEmail = (exhibitor: any) => {
  if (!exhibitor) return '-';
  return exhibitor?.email || exhibitor?.company_email || '-';
};

export const exhibitorListToOptions = (exhibitorList: any) => {
  if (!exhibitorList) return [];
  return exhibitorList.map((exhibitor: any) => {
    const name = getExhibitorDisplayName(exhibitor);
    const email = getExhibitorEmail(exhibitor);
    return {
      label: createElement(
        'div',
        { className: 'flex flex-col leading-tight' },
        createElement('span', { className: 'text-[13px] font-medium' }, name),
        createElement('span', { className: 'text-[11px] text-gray-500' }, email),
      ),
      value: exhibitor?.id,
      searchText: `${name} ${email}`.trim(),
    };
  });
};

export const eventListToOptions = (eventList: any) => {
  if (!eventList) return [];
  return eventList.map((event: any) => ({
    label: event.name,
    value: event.id,
    organiserId: event.organiser_id || event.organiser?.id || null,
  }));
};

export const countryListToOptions = (countryList: { label: string; value?: string; code?: string }[]) => {
  if (!countryList) return [];
  return countryList.map((c) => ({ label: c.label, value: c.label }));
};

export const getMemberDisplayName = (member: { first_name?: string; last_name?: string; name?: string; email?: string } | null | undefined): string => {
  if (!member) return '-';
  if (member.name) return member.name;
  return [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email || '-';
};

export const getEventList = (eventListData: any) => {
  if (!eventListData) return [];
  try {
    return eventListData.map((event: any) => ({ label: event.name, value: event.name }));
  } catch (error) {
    console.error("Error in getEventList", error);
    return [];
  }
};

export const getServiceTypes = (serviceTypesData: any) => {
  if (!serviceTypesData) return [];

  try {
    return serviceTypesData.map((serviceType: any) => ({
      label: serviceType,
      value: serviceType
    }));
  } catch (error) {
    return [];
  }

};

export const getCargoTypes = (cargoTypesData: any) => {
  if (!cargoTypesData) return [];

  try {
    return cargoTypesData.map((cargoType: any) => ({
      label: cargoType.name || cargoType,
      value: cargoType.id || cargoType
    }));
  } catch (error) {
    return [];
  }
};

/** GET /master/carriers: `{ data: { carrier_label, carrier_value }[] }` (React Query caches `response.data`). */
export const carriersApiResponseToOptions = (body: any): { label: string; value: string }[] => {

  const raw = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];

  if (!raw.length) return [];

  const out: { label: string; value: string }[] = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue;
    const label = String(c.carrier_label ?? '').trim();
    const val = String(c.carrier_value ?? '').trim();
    if (!val) continue;
    out.push({ label: label || val, value: val });
  }

  return out;

};

export const strYesNoToBool = (str: string): boolean => {
  return str === 'Yes' || str === 'yes';
};

export const booleanToStrYesNo = (bool: boolean): string => {
  return bool ? 'yes' : 'no';
};

export const cleanDataToSave = (data: any) => {
  let cleanedData = Object.fromEntries(
    Object.entries(data).filter(([_, v]) => v !== undefined && v !== "")
  );
  return cleanedData;
};

export const validateIfNotShipment = (formStatus: any) => {

  const withoutShipment = ['exhibitor_status', 'events_status', 'service_logistics', 's_i'];
  const value = "validated";

  const allValid = withoutShipment.every((key: any) => formStatus[key] === value);

  return allValid;

}

export const getDimensionsList = (dimensionsListData: any) => {
  if (!dimensionsListData) return [];
  return dimensionsListData.map((dimension: any) => ({ label: dimension?.symbol, value: dimension?.symbol }));
};



export const getWeightsList = (weightsListData: any) => {
  if (!weightsListData) return [];
  return weightsListData.map((weight: any) => ({ label: weight?.symbol, value: weight?.symbol }));
}

export const meterToCentimeter = (meter: number): number => {
  return meter * 100;
}

export const inchesToCentimeters = (inches: number): number => {
  return inches * 2.54;
}

export const lbToKg = (lb: number): number => {
  return lb * 0.4536; // 1lb = 0.4536kg
}

export const gToKg = (g: number): number => {
  return g * 0.001; // 1000g = 1kg
}


export const kgToLb = (kg: number): number => {
  return kg * 2.20462; // 1kg = 2.20462lb
}

export const priceRangeCalculation = (totalAmount: number): number => {
  return totalAmount * 10;
}

export const retLBH = (length: number, width: number, height: number, dimensionUnit: string): any => {
  if (dimensionUnit === "cm") {
    return Number(length) * Number(width) * Number(height);
  }
  if (dimensionUnit === "m") {
    return meterToCentimeter(Number(length)) * meterToCentimeter(Number(width)) * meterToCentimeter(Number(height));
  }
  if (dimensionUnit === "in") {
    return inchesToCentimeters(Number(length)) * inchesToCentimeters(Number(width)) * inchesToCentimeters(Number(height));
  }
}

export const retWInKg = (weight: number, weightUnit: string) => {
  if (weightUnit === "kg") {
    return Number(weight);
  }
  if (weightUnit === "g") {
    return gToKg(Number(weight));
  }
  if (weightUnit === "lb") {
    return lbToKg(Number(weight));
  }
}

export const extractStandPurchaseData = (allData: any) => {

  if (!allData || !allData?.services) return undefined;

  const services = allData?.services?.find((service: any) => service?.service_id === SERVICES_KEYS[0].id);

  if (!services) return undefined;

  const standPurchaseData = {
    id: SERVICES_KEYS[0].id,
    service_id: SERVICES_KEYS[0].id,
    graphics_type: services?.graphics_type,
    service_name: services.service_name,
    stand_type: services?.stand_type,
    totalPrice: services?.service_charge,
    currency_symbol: CURRENCY_CODE_TO_SYMBOL[services?.service_charge_amount_currency],
    currency: services?.service_charge_amount_currency,
  }

  return standPurchaseData;

}

export const extractStandRentalData = (allData: any) => {

  if (!allData || !allData?.services) return undefined;

  const services = allData?.services?.find((service: any) => service?.service_id === SERVICES_KEYS[1].id);

  if (!services) return undefined;

  const standPurchaseData = {
    id: SERVICES_KEYS[1].id,
    service_id: SERVICES_KEYS[1].id,
    graphics_type: services?.graphics_type,
    service_name: services.service_name,
    stand_type: services?.stand_type,
    totalPrice: services?.service_charge,
    currency_symbol: CURRENCY_CODE_TO_SYMBOL[services?.service_charge_amount_currency],
    currency: services?.service_charge_amount_currency,
  }

  return standPurchaseData;

}

export const extractStandInstallDismantleData = (allData: any) => {

  if (!allData || !allData?.services) return undefined;

  const services = allData?.services?.find((service: any) => service?.service_id === SERVICES_KEYS[2].id);

  if (!services) return undefined;

  const standInstallDismantleData = {
    id: SERVICES_KEYS[2].id,
    service_id: SERVICES_KEYS[2].id,
    graphics_type: services?.graphics_type,
    service_name: services.service_name,
    stand_type: services?.stand_type,
    totalPrice: services?.service_charge,
    currency_symbol: CURRENCY_CODE_TO_SYMBOL[services?.service_charge_amount_currency],
    currency: services?.service_charge_amount_currency,
  }

  return standInstallDismantleData;

}

export const extractYouShipToOurWarehouseData = (allData: any) => {

  if (!allData || !allData?.services) return undefined;

  const services = allData?.services?.find((service: any) => service?.service_id === SERVICES_KEYS[3].id);

  if (!services) return undefined;

  // Onsite & Shipping details
  const YonsiteNShipping = {
    onsite_contact_name: services?.onsite_contact_name,
    onsite_contact_email: services?.onsite_contact_email,
    onsite_contact_phone: services?.onsite_contact_phone,
    shipment_items: services?.shipment_items.map((item: any) => ({
      item_number: item?.item_number,
      dimension_unit: item?.dimension_unit,
      weight_unit: item?.weight_unit,
      length: item?.length,
      width: item?.width,
      height: item?.height,
      weight: item?.weight,
      description: item?.description || "",
      lbh_calculation: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit),
      volumetric_weight: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit) / 5000,
      w_in_kg: retWInKg(item?.weight, item?.weight_unit)
    }))
  }

  // Stand & Collection details
  const YstandNCollection = {
    hall_stand_number: services?.hall_stand_number,
    tracking_number: services?.tracking_number,
    tracking_carrier: services?.tracking_carrier,
    collection_contact_name: services?.collection_contact_name,
    collection_address: services?.collection_address,
    goods_return: services?.goods_return === false ? 'no' : 'yes',
    return_shipment_date: services?.return_shipment_date,
  }

  const YinsuranceNOthers = {
    insurance_required: services?.insurance_required === false ? 'no' : 'yes',
    special_instructions: services?.special_instructions,
    empty_case_storage: services?.empty_case_storage === false ? 'no' : 'yes',
    items_list: services?.empty_case_items?.map((item: any, index: number) => ({
      item_number: index + 1,
      dimension_unit: item?.dimension_unit,
      length: item?.length,
      width: item?.width,
      height: item?.height,
      description: item?.description,
      lbh_calculation: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit),
      volumetric_weight: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit) / 5000
    }))
  }

  return { YonsiteNShipping, YstandNCollection, YinsuranceNOthers };

}

export const extractWeCollectFromYouData = (allData: any) => {

  if (!allData || !allData?.services) return undefined;

  const services = allData?.services?.find((service: any) => service?.service_id === SERVICES_KEYS[4].id);

  if (!services) return undefined;

  // Onsite & Shipping details
  const onsiteNShipping = {
    onsite_contact_name: services?.onsite_contact_name,
    onsite_contact_email: services?.onsite_contact_email,
    onsite_contact_phone: services?.onsite_contact_phone,
    shipment_items: services?.shipment_items.map((item: any) => ({
      item_number: item?.item_number,
      dimension_unit: item?.dimension_unit,
      weight_unit: item?.weight_unit,
      length: item?.length,
      width: item?.width,
      height: item?.height,
      weight: item?.weight,
      description: item?.description || "",
      lbh_calculation: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit),
      volumetric_weight: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit) / 5000,
      w_in_kg: retWInKg(item?.weight, item?.weight_unit)
    }))
  }

  // Stand & Collection details
  const standNCollection = {
    hall_stand_number: services?.hall_stand_number,
    collection_reference_number: services?.collection_reference_number,
    collection_address: services?.collection_address,
    collection_contact_name: services?.collection_contact_name,
    goods_ready_date: services?.goods_ready_date,
    goods_return: services?.goods_return === false ? 'no' : 'yes',
    return_shipment_date: services?.return_shipment_date,
  }

  const insuranceNOthers = {
    insurance_required: services?.insurance_required === false ? 'no' : 'yes',
    special_instructions: services?.special_instructions,
    empty_case_storage: services?.empty_case_storage === false ? 'no' : 'yes',
    items_list: services?.empty_case_items?.map((item: any, index: number) => ({
      item_number: index + 1,
      dimension_unit: item?.dimension_unit,
      length: item?.length,
      width: item?.width,
      height: item?.height,
      description: item?.description,
      lbh_calculation: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit),
      volumetric_weight: retLBH(item?.length, item?.width, item?.height, item?.dimension_unit) / 5000
    }))
  }

  return { onsiteNShipping, standNCollection, insuranceNOthers };

}

export const extractServiceList = (allData: any) => {
  if (!allData || !allData?.services) return undefined;
  const listOfServiceIds = allData?.services ? allData?.services.map((service: any) => service?.service_key) : [];
  return listOfServiceIds;
}

export const calcItemsForRegionGetTotalAmount = (itemsList: any, eventData: any) => {

  const priceRangeCalc = (cbmTotal: number, chargebleCbm: number) => {

    const region = eventData?.event_region;
    const priceRangeCalculationData = CBM_PRICE_RANGE_CALCULATION.find((item: any) => item.region === region);
    const amount = chargebleCbm * priceRangeCalculationData?.metaData?.pricePCBM;

    return amount?.toFixed(2);

  }

  let cbmTotal = 0;

  for (let eachItem of itemsList) {
    let eachWidth;
    let eachLength;
    let eachHeight;

    if (eachItem.dimension_unit === "cm") {
      eachWidth = eachItem.width;
      eachLength = eachItem.length;
      eachHeight = eachItem.height;
    }

    if (eachItem.dimension_unit === "m") {
      eachWidth = eachItem.width * 100;
      eachLength = eachItem.length * 100;
      eachHeight = eachItem.height * 100;
    }

    if (eachItem.dimension_unit === "in") {
      eachWidth = eachItem.width * 2.54;
      eachLength = eachItem.length * 2.54;
      eachHeight = eachItem.height * 2.54;
    }

    cbmTotal += (eachItem.width * eachItem.length * eachItem.height) / 1000000;

  }

  const chargebleCbm = cbmTotal >= 2 ? cbmTotal : 2;
  const finalAmount = priceRangeCalc(cbmTotal, chargebleCbm);

  return finalAmount;

}

export const addressFormatter = (warehouse: any, address: any) => {
  if (!address || address === null || address === undefined || address === '' || address === 'null' || address === 'undefined') return "_";

  const parts = [
    address?.address_line_1,
    address?.address_line_2,
    address?.address_line_3,
    address?.city,
    address?.state,
    address?.country,
    address?.postal_code
  ].filter(part => part && part !== 'null' && part !== 'undefined');

  const formattedAddress = parts.join(', ');

  return warehouse?.name ? ` ${warehouse.name} ${formattedAddress}` : ` ${formattedAddress}`;
}


export const inboundServiceTypes = [
  { label: "D2D", value: "D2D" },
  { label: "NOT APPLICABLE", value: "NOT APPLICABLE" },
  { label: "R&D", value: "R&D" },
  { label: "NOT REQUIRED", value: "NOT REQUIRED" },
  { label: "YARD TO STAND", value: "YARD TO STAND" },
  { label: "FORK LIFT ONLY", value: "FORK LIFT ONLY" }
]

export const outboundServiceTypes = [
  { label: "R&D", value: "R&D" },
  { label: "NOT REQUIRED", value: "NOT REQUIRED" },
  { label: "YARD TO STAND", value: "YARD TO STAND" },
  { label: "FORK LIFT ONLY", value: "FORK LIFT ONLY" },
  { label: "NOT APPLICABLE", value: "NOT APPLICABLE" },
]
export const outboundServiceTypesMannual = [
  { label: "D2D", value: "D2D" },
  { label: "R&D", value: "R&D" },
  { label: "NOT REQUIRED", value: "NOT_REQUIRED" },
  { label: "YARD TO STAND", value: "YARD_TO_STAND" },
  { label: "FORK LIFT ONLY", value: "FORK_LIFT_ONLY" },
  { label: "NOT APPLICABLE", value: "NOT_APPLICABLE" },
]

export const updateType = {
  preAlert: 'pre_alert',
  preAlertUpdate: 'return_pre_alert',
  enquiryUpdate: 'enquiry_update',
  programmeManagerUpdate: 'programme_manager_update',
}

export const tripPathFromUrl = (url: string) => {
  if (typeof url !== 'string') return "Error";
  if (!url) return "_";
  return url.split('/').pop() || "_";
}

export const monthListToOptions = () => {
  return [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ]
}

export const yearListToOptions = () => {
  return [
    { label: "2025", value: 2025 },
    { label: "2026", value: 2026 },
    { label: "2027", value: 2027 },
    { label: "2028", value: 2028 },
    { label: "2029", value: 2029 },
    { label: "2030", value: 2030 },
  ]
}

export { StatusBadge } from './StatusBadge';

export const serviceTypeToText = (serviceType: string) => {
  const serviceTypeMap: Record<string, string> = {
    'stand_purchase': 'Stand Purchase',
    'stand_rental': 'Stand Rental',
    'stand_install_dismantle': 'Stand Install/Dismantle',
    'freight_(door_to_door)': 'Freight (Door to Door)',
    'freight_(via_warehouse)': 'Freight (Via Warehouse)',
    'OTHER': 'Other',
    'other': 'Other',
    'others': 'Other',
    'Others': 'Other',
  }
  return serviceTypeMap[serviceType] || serviceType;
}


export const normalizeEventTags = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t ?? '').trim());
  }
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
};

export const isHansonWadeOrganiserName = (name: unknown) => {
  
  if (!name) return false;
  if (typeof name !== 'string') return false;
  if (name === '') return false;
  const value = name.trim().toLowerCase().replace(' ', '_');
  return value === 'hanson_wade';

}

export const toHansonWadeEventTabKey = (value: unknown): 'Life Science' | 'Construction' => {
  if (value === 'Construction') return 'Construction';
  return 'Life Science';
};

export const eventMatchesHansonWadeTab = (event: any, tab: string) => {
  const tabValue = toHansonWadeEventTabKey(tab);
  const tags = Array.isArray(event?.tags) ? event.tags : [];
  return tags.includes(tabValue);
};

export const filterEventsByName = (events: any[], searchText: string) => {
  if (!searchText?.trim()) return events;
  const query = searchText.trim().toLowerCase();
  return events.filter((event: any) => event?.name?.toLowerCase().includes(query));
};

